import React, { FC, useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import Comment from './Comment';
import deleteCommentMutation from '../mutations/deleteComment';
import { CommentDeleteConfirmationModal } from '../CommentDeleteConfirmationModal/CommentDeleteConfirmationModal';
import { useModal } from '../../Modal/useModal';
import { TCommentProps } from '../CommentsList';
import CommentsThread from '../CommentsThread';
import { getFeatureFlag } from '../../FeatureFlags/getFeatureFlag';
import CommentSeparator from './CommentSeparator';
import { CommentsContext } from '../CommentsContext';
import CommentReplyForm from './CommentReplyForm';
import { useGetComments } from '../hooks/useGetComments';
import { AnalyticsModalType } from '../../Modal/constants';
import { CommentsListType } from '../constants';

export type TCommentContainerProps = Omit<TCommentProps, 'parentComment'> & {
  isLastComment?: boolean;
  isCommentAuthor: boolean;
  parentCommentId?: string;
  commentsListType: CommentsListType;
};

const CommentContainer: FC<TCommentContainerProps> = ({
  id,
  parentCommentId,
  isLastComment,
  origin,
  hasT2ChildComments,
  commentsListType,
  ...props
}) => {
  const { articleId, loadingAuthenticatedUserStatus, userId } = useContext(CommentsContext);
  const { openModal, ModalComponent, closeModal, isOpen } = useModal({
    analytics: { modalName: AnalyticsModalType.commentDelete },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCommentThreadOpen, setIsCommentThreadOpen] = useState(false);
  const [isReplyToCommentOpen, setIsReplyToCommentOpen] = useState(false);
  const showCommentsThread = getFeatureFlag('commentsThread');
  const { loading, error, comments, getT2Comments, shouldFetchT2Comments } = useGetComments({
    articleId,
    loadingAuthenticatedUserStatus,
    userId,
  });

  const [deleteComment] = useMutation(deleteCommentMutation, {
    variables: {
      commentId: id,
    },
  });

  const handleDeleteConfirm = async () => {
    setIsLoading(true);
    const { errors } = await deleteComment();

    if (errors) {
      setIsLoading(false);
    }

    if (!errors) {
      closeModal();
      loadCommentsAfterDelete();
    }

    setIsLoading(false);
  };

  const loadCommentsAfterDelete = () => {
    const commentId = origin === 't2' ? parentCommentId : id;
    const isRootComment = !parentCommentId;
    const variables = {
      parentCommentId: isRootComment ? undefined : commentId,
    };

    if (shouldFetchT2Comments) {
      getT2Comments({ variables });
    }
  };

  const loadComments = () => {
    const variables = { parentCommentId: id };

    if (shouldFetchT2Comments) {
      getT2Comments({
        variables,
      });
    }
  };

  const handleOpenCommentThread = () => {
    setIsReplyToCommentOpen(false);

    if (!isCommentThreadOpen) {
      loadComments();
    }

    setIsCommentThreadOpen(!isCommentThreadOpen);
  };

  const handleReplyToComment = () => {
    setIsReplyToCommentOpen(!isReplyToCommentOpen);

    if (!isCommentThreadOpen) {
      setIsCommentThreadOpen(true);
      loadComments();
    }
  };

  const handleReplyToCommentSubmit = () => {
    setIsReplyToCommentOpen(false);
    loadComments();
  };

  const handleDiscardComment = () => {
    setIsReplyToCommentOpen(false);
  };

  return (
    <>
      <Comment
        id={id}
        onDeleteButtonClick={openModal}
        handleOpenCommentThread={handleOpenCommentThread}
        parentCommentId={parentCommentId}
        loading={loading}
        handleReplyToComment={handleReplyToComment}
        origin={origin}
        {...props}
      />
      {showCommentsThread && (
        <CommentReplyForm
          articleId={articleId}
          commentId={id}
          handleReplyToCommentSubmit={handleReplyToCommentSubmit}
          isVisible={isReplyToCommentOpen}
          onDiscardButtonClick={handleDiscardComment}
          commentOrigin={origin}
          parentCommentId={parentCommentId}
        />
      )}
      {isOpen && (
        <CommentDeleteConfirmationModal
          ModalComponent={ModalComponent}
          onConfirm={handleDeleteConfirm}
          onCancel={closeModal}
          isLoading={isLoading}
        />
      )}
      {showCommentsThread && (
        <CommentsThread
          isCommentThreadOpen={isCommentThreadOpen}
          comments={comments}
          loading={loading}
          error={error}
          commentsListType={commentsListType}
        />
      )}
      {!parentCommentId && origin === 't2' && !isLastComment && <CommentSeparator />}
    </>
  );
};

export default CommentContainer;
